import React from 'react';
import FilesSection from './FilesSection';
import TradesSection from './TradesSection';
interface TradeType {
    id: string;
    category: 'UA' | 'NON-UA';
    name: string;
    description: string | null;
    createdAt: string;
    updatedAt: string;
}

interface Contractor {
    id: string;
    name: string;
    email: string;
    cellNumber: string | null;
    isSignatory: boolean;
    isMultiTrade: boolean;
    status: string;
    additionalNotes: string | null;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    contractorUnionType: string | null;
}

interface TradeContractAssociation {
    id: string;
    noOfJourneymen: number;
    noOfApperntices: number;
    createdAt: string;
    updatedAt: string | null;
    contractor: Contractor;
    tradeType: TradeType;
}

interface JobTrade {
    id: string;
    unionStatus: 'union' | 'clac' | 'merit' | 'other' | 'mixed';
    isSignatory: boolean;
    numOfWorkers: number;
    isUACanadaTrade: boolean;
    createdAt: string;
    updatedAt: string | null;
    tradeType: TradeType;
    localUnionIds: Array<{
        id: string;
        code: string;
        unionNumber: number;
        createdAt: string;
        updatedAt: string;
    }>;
    tradeContractAssociations: TradeContractAssociation[];
}

interface JobsiteTradeResponse {
    id: string;
    noOfTrades: string;
    createdAt: string;
    updatedAt: string | null;
    jobTrade: JobTrade[];
}
interface JobsiteVisitsSectionProps {
    visitDate?: Date | string;
    totalWorkers?: number;
    jobsiteVisitDetails?: {
        jobCompletionStatus?: { status?: string };
        comments: string[];  // Always an array of strings
    };
    getStatusCompletionPercentage: (status: string) => string;
    getCompletionPercentage: (status?: string) => number;
    jobsiteFiles: any[];
    jobsiteTradeData: JobsiteTradeResponse | null;
    jobDetails: {
        id?: string;
        contractor?: { name: string };
        generalContractor?: { name: string };
        siteContact: string;
        siteContactEmail: string;
        jobDetailsSectors: Array<{ sector: { name: string } }>;
        industry: { name: string };
        jobCompletionStatus: { status: string };
    };
    handleViewFile: (fileId: string) => void;
    title: string;
}


const JobsiteVisitsSection: React.FC<JobsiteVisitsSectionProps> = ({ title, visitDate, totalWorkers, jobDetails, jobsiteVisitDetails, getCompletionPercentage, jobsiteFiles, jobsiteTradeData, handleViewFile }) => {
    return (
        <>
            <div className='bg-white rounded-md shadow mb-4'>
                    <div className='p-4 sm:p-6'>
                        <div>
                            <h2 className='text-[20px] font-[700] mb-3'>{title}</h2>

                            {/* Visit Date */}
                            {visitDate && (
                                <div className='mb-4'>
                                    <p className="text-gray-500 text-sm">Visit Date</p>
                                    <p className="font-medium text-gray-900">
                                        {new Date(visitDate).toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            timeZone: 'UTC'
                                        })}
                                    </p>
                                </div>
                            )}

                            {/* Total Workers */}
                            {totalWorkers !== undefined && totalWorkers !== null && (
                                <div className='mb-4'>
                                    <p className="text-gray-500 text-sm">Total Workers On Site</p>
                                    <p className="font-medium text-gray-900 text-lg">
                                        {totalWorkers} {totalWorkers === 1 ? 'worker' : 'workers'}
                                    </p>
                                </div>
                            )}

                            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                                <div className='mb-3'>
                                    {/* Job Completion */}
                                        <span className='text-[16px] font-[700] text-black'>Completion Status</span>
                                    <div className='flex items-center justify-between mb-2'>
                                        <p className="font-medium text-gray-900 text-lg">{jobsiteVisitDetails?.jobCompletionStatus?.status}</p>
                                        <span className='text-sm sm:text-md'>{getCompletionPercentage(jobsiteVisitDetails?.jobCompletionStatus?.status)}%</span>
                                    </div>
                                    <div className='relative mb-2'>
                                        <div className='w-full bg-gray-200 h-2 rounded-full'>
                                            <div
                                                className='absolute left-0 top-0 bg-[#A42823] h-2 rounded-full'
                                                style={{ width: `${getCompletionPercentage(jobsiteVisitDetails?.jobCompletionStatus?.status)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <TradesSection trades={jobsiteTradeData} />
                                <FilesSection jobsiteFiles={jobsiteFiles} handleViewFile={handleViewFile} />

                                {/* Comment Section */}
                                {(() => {
                                    // Filter out empty comments (comments is always string[])
                                    const validComments = jobsiteVisitDetails?.comments?.filter(
                                        (comment: string) => comment && comment.trim().length > 0
                                    ) || [];

                                    if (validComments.length === 0) return null;

                                    return (
                                        <div className="mt-6 pt-6 border-t border-gray-200">
                                            <h3 className="text-[16px] font-[700] text-black mb-3">Comment</h3>
                                            <div className="space-y-3">
                                                {validComments.map((comment: string, index: number) => (
                                                    <p key={index} className="text-gray-800">
                                                        {comment}
                                                    </p>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
            </div>
        </>
    );
};

export default JobsiteVisitsSection; 